import { isServer } from '@rewardops/utils'

import {
  AcAttributeErrorParams,
  AcAttributeParams,
  AcEventInfoParams,
  AcEventParams,
  ProductInfoParams,
} from './air-canada-types'

declare global {
  interface Window {
    digitalDataAC: any
  }
}

export function convertToProductsInfo(input: ProductInfoParams[]) {
  return input.map(convertToProductInfo)
}

export function convertToProductInfo(input: ProductInfoParams) {
  const {
    itemDetail,
    quantity = null,
    promotionalTagData,
    adobeStringCategory,
  } = input

  // Assuming `cash_points` is a static value for productPaymentOptions as it's not explicitly provided in the input
  const productPaymentOptions = 'cash_points'

  // Extracting the brand information
  const brand = itemDetail.brands.map((brand) => brand.key).join('-')

  // Assuming the first category and subcategory can be determined by splitting the first category path
  const categories =
    itemDetail.categories[0]?.path.split('/').filter((p) => p) || []
  const category = categories.length > 1 ? categories[1] : ''
  const subCategory = categories.length > 2 ? categories[2] : ''

  // Price needs to be extracted from the `supplier_items` assuming the first item's `cash` property
  const price = parseFloat(
    itemDetail.supplier_items[0]?.cash.cash_price.CAD ?? '0',
  )
  const points = parseInt(
    itemDetail.supplier_items[0]?.prices.regular_price['XRO-APM'] ?? '0',
  )

  return {
    adobeStringCategory,
    name: itemDetail.name.toLowerCase(),
    id: itemDetail.id,
    price,
    quantity,
    points,
    productPaymentOptions,
    productClassification: itemDetail.type,
    brand,
    category,
    subCategory,
    listName: null,
    promotionalTag: promotionalTagData,
    position: input.position ?? null,
  }
}

export function formatAcTimeStamp(date: Date): string {
  const pad = (num: number) => (num < 10 ? `0${num}` : num.toString())

  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1) // JavaScript months are 0-based.
  const day = pad(date.getDate())
  const hours = date.getHours()
  const minutes = pad(date.getMinutes())

  // Convert 24h to 12h format
  const hours12 = hours % 12 === 0 ? 12 : hours % 12
  const amPm = hours < 12 ? 'am' : 'pm'

  // Day names array
  const days = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]
  const dayName = days[date.getDay()]

  return `${year}-${month}-${day} ${dayName}_${hours12}:${minutes}_${amPm}`
}

export function pushAcEvent(eventParams?: AcEventParams, includeEvent = true) {
  if (isServer()) return

  // Initialize the global digitalDataAC object if it doesn't exist
  if (!window.digitalDataAC || !window.digitalDataAC.events) {
    window.digitalDataAC = window.digitalDataAC ?? {}
    window.digitalDataAC.events = window.digitalDataAC.events ?? []
  }

  const event: any = {
    attributes: {
      ...acAttributes(eventParams?.attributes),
      ...eventParams?.extraArgs,
    },
  }

  if (includeEvent) {
    event.eventInfo = acEventInfo(eventParams?.event)
  }

  window.digitalDataAC.events.push(event)
}

const acEventInfo = (params?: AcEventInfoParams) => {
  const {
    eventName = 'apple-estore-pageview',
    event = 'pageview',
    dataLayerID,
  } = params ?? {}

  return {
    eventName,
    event,
    dataLayerID,
    timeStamp: new Date(),
  }
}

export function acAttributes(
  attributeParams?: AcAttributeParams | AcAttributeErrorParams,
) {
  if (!attributeParams) {
    return undefined
  }

  if ('errorInfo' in attributeParams) {
    const { errorInfo } = attributeParams
    return {
      errorInfo: {
        errorCode: errorInfo.errorCode,
        errorType: errorInfo.errorType,
        errorDescription: errorInfo.errorDescription,
      },
    }
  }

  const { pageHierarchy, pageType, pageTitle } = attributeParams
  const level1 = pageHierarchy.level1 ?? 'aeroplan_apple'

  const fullUrl = window.location.href

  // Remove query params from the URL
  const url = fullUrl.split('?')[0]

  return {
    pageInfo: {
      pageName: `${level1}|${pageHierarchy.level2}${
        pageHierarchy.level3 ? `|${pageHierarchy.level3}` : ''
      }`,
      pageType,
      pageTitle,
      url,
      fullUrl,
      pageHierarchy: {
        level1,
        ...pageHierarchy,
      },
      accessibility: 'true',
      timeStamp: formatAcTimeStamp(new Date()),
    },
  }
}

export const getScreenSize = () => {
  const width = Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth,
  )
  switch (true) {
    case width < 640:
      return 'small'
    case 641 <= width && width < 1007:
      return 'medium'
    case 1008 <= width:
      return 'large'
  }
}
